import React, { useEffect, useMemo, useState } from "react";
import { Download, FileText, FolderClosed, LockKeyhole, Pencil, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";

function errorDetail(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

function sizeLabel(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function FileLibrary({ dealId = null }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ deal_id: dealId || "", set_name: "", document_type: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [files, dealItems] = await Promise.all([
        api.listFiles(dealId),
        dealId ? Promise.resolve([]) : api.listDeals(),
      ]);
      setItems(files);
      setDeals(dealItems);
    } catch (error) {
      toast.error(errorDetail(error, t("files_load_failed")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  const dealById = useMemo(() => Object.fromEntries(deals.map((deal) => [deal.id, deal])), [deals]);
  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = items.filter((item) => !term || [item.display_name, item.document_type, item.set_name]
      .some((value) => value?.toLowerCase().includes(term)));
    return Object.entries(filtered.reduce((result, item) => {
      const key = item.set_name || t("files_unfiled");
      (result[key] ||= []).push(item);
      return result;
    }, {}));
  }, [items, search, t]);

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!selected) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("upload", selected);
      if (form.deal_id) body.append("deal_id", form.deal_id);
      if (form.set_name.trim()) body.append("set_name", form.set_name.trim());
      if (form.document_type.trim()) body.append("document_type", form.document_type.trim());
      const created = await api.uploadFile(body);
      setItems((value) => [created, ...value]);
      setSelected(null);
      setForm((value) => ({ ...value, set_name: "", document_type: "" }));
      event.target.reset();
      toast.success(t("files_uploaded"));
    } catch (error) {
      toast.error(errorDetail(error, t("files_upload_failed")));
    } finally {
      setUploading(false);
    }
  };

  const rename = async (item) => {
    const next = window.prompt(t("files_rename_prompt"), item.display_name)?.trim();
    if (!next || next === item.display_name) return;
    try {
      const updated = await api.updateFile(item.id, { display_name: next });
      setItems((value) => value.map((entry) => entry.id === item.id ? updated : entry));
    } catch (error) {
      toast.error(errorDetail(error, t("files_update_failed")));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(t("files_delete_confirm"))) return;
    try {
      await api.deleteFile(item.id);
      setItems((value) => value.filter((entry) => entry.id !== item.id));
      toast.success(t("files_deleted"));
    } catch (error) {
      toast.error(errorDetail(error, t("files_delete_failed")));
    }
  };

  return (
    <div>
      <form onSubmit={submitUpload} data-testid={TESTIDS.fileUploadForm} className="card-surface p-6 mb-7">
        <div className="flex items-center gap-2 mb-5">
          <Upload size={17} className="text-[var(--td-gold)]" />
          <h2 className="font-serif text-2xl">{t("files_upload_title")}</h2>
        </div>
        <div className={`grid ${dealId ? "lg:grid-cols-[2fr_1fr_1fr_auto]" : "lg:grid-cols-[2fr_1.2fr_1fr_1fr_auto]"} gap-3 items-end`}>
          <div>
            <label className="td-label">{t("files_file")}</label>
            <input required type="file" data-testid={TESTIDS.fileInput}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.tif,.tiff,.eml,.msg"
              onChange={(event) => setSelected(event.target.files?.[0] || null)}
              className="td-input !py-2 text-xs file:mr-3 file:border-0 file:bg-transparent file:text-[var(--td-gold)]" />
          </div>
          {!dealId && (
            <div>
              <label className="td-label">{t("files_deal")}</label>
              <select className="td-input" value={form.deal_id} onChange={(event) => setForm({ ...form, deal_id: event.target.value })}>
                <option value="">{t("files_no_deal")}</option>
                {deals.map((deal) => <option key={deal.id} value={deal.id}>{deal.reference || deal.title}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="td-label">{t("files_set")}</label>
            <input className="td-input" maxLength={100} placeholder={t("files_set_placeholder")}
              value={form.set_name} onChange={(event) => setForm({ ...form, set_name: event.target.value })} />
          </div>
          <div>
            <label className="td-label">{t("files_type")}</label>
            <input className="td-input" maxLength={80} placeholder={t("files_type_placeholder")}
              value={form.document_type} onChange={(event) => setForm({ ...form, document_type: event.target.value })} />
          </div>
          <button type="submit" disabled={!selected || uploading} data-testid={TESTIDS.fileUpload}
            className="btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap">
            <Upload size={13} /> {uploading ? t("files_uploading") : t("files_upload")}
          </button>
        </div>
        <p className="mt-3 text-[11px] text-[var(--td-text-3)]">{t("files_limit_hint")}</p>
      </form>

      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)]">
          {items.length} {t("files_items")}
        </div>
        <label className="relative max-w-sm flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--td-text-3)]" />
          <input className="td-input !pl-9 !py-2" value={search} onChange={(event) => setSearch(event.target.value)}
            placeholder={t("files_search")} />
        </label>
      </div>

      {loading ? <div className="card-surface p-8 text-[var(--td-text-3)]">…</div> : groups.length === 0 ? (
        <div className="card-surface p-14 text-center">
          <FolderClosed size={30} className="mx-auto mb-3 text-[var(--td-gold)]" />
          <div className="font-serif text-xl">{t("files_empty")}</div>
          <p className="text-sm text-[var(--td-text-3)] mt-2">{t("files_empty_hint")}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([group, files]) => (
            <section key={group} className="card-surface overflow-hidden">
              <div className="px-5 py-3 border-b border-[var(--td-border)] flex items-center gap-2">
                <FolderClosed size={14} className="text-[var(--td-gold)]" />
                <h3 className="font-mono text-[11px] uppercase tracking-widest">{group}</h3>
                <span className="text-[10px] text-[var(--td-text-3)]">{files.length}</span>
              </div>
              {files.map((item) => (
                <div key={item.id} data-testid={TESTIDS.fileRow} className="p-5 border-b border-[var(--td-border)] last:border-b-0 flex items-center gap-4">
                  <FileText size={20} className="text-[var(--td-text-3)] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-lg truncate">{item.display_name}</div>
                    <div className="flex flex-wrap gap-x-3 text-[10px] font-mono uppercase tracking-wider text-[var(--td-text-3)] mt-1">
                      <span>{item.extension.slice(1)} · {sizeLabel(item.size_bytes)}</span>
                      {item.document_type && <span>{item.document_type}</span>}
                      {item.immutable && <span className="text-emerald-400 inline-flex items-center gap-1"><LockKeyhole size={10} /> {t("files_immutable")}</span>}
                      {item.sha256 && <span title={item.sha256}>SHA-256 {item.sha256.slice(0, 12)}…</span>}
                      {!dealId && item.deal_id && <span className="text-[var(--td-gold)]">{dealById[item.deal_id]?.reference || t("files_deal")}</span>}
                      <span>{new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {!item.immutable && <button type="button" onClick={() => rename(item)} aria-label={t("files_rename")} className="text-[var(--td-text-3)] hover:text-[var(--td-gold)]"><Pencil size={15} /></button>}
                  <button type="button" onClick={() => api.downloadFile(item)} data-testid={TESTIDS.fileDownload} aria-label={t("files_download")} className="text-[var(--td-text-3)] hover:text-[var(--td-gold)]"><Download size={16} /></button>
                  {!item.immutable && <button type="button" onClick={() => remove(item)} data-testid={TESTIDS.fileDelete} aria-label={t("files_delete")} className="text-[var(--td-text-3)] hover:text-red-400"><Trash2 size={15} /></button>}
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
